import {React, observer, Highlighter, useAsObservableSource, useLocalStore, jsonComputedOptions, useLayoutEffect, Suspense, useEffect} from '@api'
import {Button} from '@blueprintjs/core'
import {useCatalogItemSidebarDetailsStore, CatalogItemSidebarDetailsStore, LoadingIndicator} from '@modules'
import {ModalTabConfig} from '@stores'
import {computed, observable, trace} from 'mobx'
import {useContext} from 'react'
import {useMeasure} from 'react-use'
import * as css from './ModalDetailTabs.css'
import {ModalDetailSection} from './ModalDetailSection'
import {CatalogSecondariesDetails, CustomDetailsTemplate} from './'
import {CatalogFieldRenderer} from '@lazy'

export const CatalogItemModalDetailTabs = observer<React.HTMLProps<HTMLDivElement>>(function CatalogItemModalDetailTabs({className}) {
  const detailsStore = useCatalogItemSidebarDetailsStore(),
    {es, seenTabs, selectedDetailTab, isCreating, isSearching, data, modal: {extraTabHeader, config: {tabs}}, sidebar: {setSelectedTab, searchWords}} = detailsStore
  //console.log({tab, searchTabIndex, searchResults, selectedDetailTab: toJS(selectedDetailTab)})

  return (
    <div className={css.root} data-grid-area='tabs' data-tab={selectedDetailTab?.id}>
      <div className={css.header}>
        {(tabs ?? [])
          .filter(t => t && (!t.create || !isCreating)) // Turn off some tabs during creation flow
          .map(
            ({id, label, fields, countField, create}, i) => {
              const text = label ? label : _.capitalize(id),
                isSearchResult = isSearching && detailsStore.searchResults.some(r => r.tab == id),
                active = (!selectedDetailTab && i == 0 || id == selectedDetailTab.id), // Selected due to search
                count = countField && (es.readField(data, countField)?.val?.length ?? 0)

              return (
                <Button key={id}
                        tabIndex={0}
                        className={css.tabButton}
                        minimal
                        text={!isSearching ? text : <Highlighter textToHighlight={text} searchWords={searchWords}/>}
                        rightIcon={_.isNumber(count) && <div className={css.count}>({count.toLocaleString()})</div>}
                        active={active}
                        data-tab-label={text}
                        data-is-search-result={isSearching ? isSearchResult : undefined}
                        onMouseOver={() => seenTabs.add(id)}
                        onClick={setSelectedTab(id)}
                />)
            })}

        <div className={css.extraTabHeader}>
          {extraTabHeader}
          {isSearching === true && detailsStore.searchResults.length > 0 && <span className={css.text}>{detailsStore.searchResults.length.toLocaleString()} search result{detailsStore.searchResults.length != 1 ? 's' : ''}</span>}
        </div>
      </div>
      <CatalogItemModalDetailTabsContents/>

      <div className={css.imagePreviewHover} id='magnify-target'/>
    </div>
  )
})

const CatalogItemModalDetailTabsContents = observer<React.HTMLProps<HTMLDivElement>>(function CatalogItemModalDetailTabsContents() {
  const {modal: {config: {tabs}}, isCreating, sidebar: {selectedTab}, seenTabs, selectedDetailTab} = useCatalogItemSidebarDetailsStore()
  // console.log("tabs::",tabs, tab)
  return (
    <div className={css.tabs} data-count={tabs?.length}>
      {tabs?.filter(t => t && (!t.create || !isCreating || t.id == selectedTab)) // Turn off some tabs during creation flow
        ?.map(tab => (tab.preload || seenTabs.has(tab.id) || selectedDetailTab.id === tab.id) && <CatalogItemModalDetailTabContents tab={tab} key={tab.id}/>)}
    </div>
  )
})

export class CatalogItemModalDetailTabContentsStore {
  constructor(public props: { tab: ModalTabConfig }, public detailsStore: CatalogItemSidebarDetailsStore) {}

  @computed get isSearchResult() {
    const {detailsStore, detailsStore: {isSearching}, props: {tab: {id}}} = this
    return isSearching && detailsStore.searchResults.some(sr => sr.tab == id)
  }

  @computed get isCurrentSearchResult() {
    const {isSearchResult, detailsStore: {currentSearchResult}, props: {tab: {id}}} = this

    return isSearchResult && currentSearchResult.tab == id
  }

  @computed get sections() {
    const {props: {tab}} = this
    let sections = tab.sections
    if (tab.type) {
      sections = [...(sections ?? []), {type: tab.type}]
    }
    // if(!_.isEmpty(secondaries)) {
    //   sections = [...(sections ?? []), ...(secondaries ?? [])]
    // }
    // console.log("\n\n\n\nsections::", sections)
    return sections
  }

  @observable width: number

  @computed get responsiveColumns() {
    const {width} = this
    return width <= 300 ? 1 : width <= 400 ? 2 : undefined
  }
}

export const CatalogItemModalDetailTabContentsStoreContext = React.createContext<CatalogItemModalDetailTabContentsStore>(undefined)
export const useCatalogItemModalDetailTabContentsStore = () => useContext(CatalogItemModalDetailTabContentsStoreContext)

const CatalogItemModalDetailTabContents = observer<{ tab: ModalTabConfig }>(function CatalogItemModalDetailTabContents({tab}) {
  const detailsStore = useCatalogItemSidebarDetailsStore(),
    {id, fields, columns} = tab,
    obsProps = useAsObservableSource({tab}),
    store = useLocalStore(() => new CatalogItemModalDetailTabContentsStore(obsProps, detailsStore)),
    {responsiveColumns, detailsStore: {strings, selectedDetailTab, isSearching}, isCurrentSearchResult, sections, isSearchResult} = store

  useLayoutEffect(() => {
    setTimeout(() => {
      if (detailsStore.hasSecondariesTab && (detailsStore.secondariesStore?.secondaries?.length > 0 || detailsStore.secondariesStore?.loading === false)) {
        detailsStore.isFirstRender = false
      }
      if (detailsStore.hasAdoptionTab && detailsStore.data?.secondaries?.length > 0) {
        detailsStore.isFirstRender = false
      }
    }, 10)
  }, [detailsStore.secondariesStore?.secondaries, detailsStore.data?.secondaries])

  const [ref, {width, height}] = useMeasure()
  useLayoutEffect(() => {
    store.width = width
  }, [width])

  return (
    <CatalogItemModalDetailTabContentsStoreContext.Provider value={store}>
    <div
      ref={ref}
      data-active={selectedDetailTab.id == id}
      data-active-tab-label={selectedDetailTab.label}
      data-is-search-result={isSearching ? isSearchResult : undefined}
      data-is-current-search-result={isSearching ? isCurrentSearchResult : undefined}
      data-has-sections={sections?.length > 0}
      role='tab'
      key={id}
      className={css.tabContents}
      data-width={width}
      data-columns={responsiveColumns ?? (columns || 2)}>
      <Suspense fallback={<LoadingIndicator/>}>
        {/* {false && id == 'comments' &&*/}
        {/*<div className={css.comments}>*/}
        {/*  <CatalogItemComments className={css.commentWindow}/>*/}
        {/*  <CommentEditor className={css.editor}/>*/}
        {/*</div>*/}
        {/* }*/}

        {/*{id == 'images' && <CatalogItemImagesTab />}*/}
        {fields?.length && selectedDetailTab.id !== 'secondaries' &&
		 <div className={css.fields} data-count={fields?.length ?? 0}>
           {fields?.map((f, j) => <CatalogFieldRenderer tabId={id} className={css.field} align='modal' key={f.field ?? j} field={f}/>)}
		 </div>}
        {sections?.length && sections.map((s, i) => s.type === 'secondaries' ? <CatalogSecondariesDetails key={i}/> : <ModalDetailSection index={i} tabId={id} section={s} key={i}/>)}

        {tab.template ? <CustomDetailsTemplate template={tab.template} /> : !fields?.length && !sections?.length && <div className={css.blank}>
          {strings.intentionallyBlank}
		</div>}
      </Suspense>
    </div>
    </CatalogItemModalDetailTabContentsStoreContext.Provider>
  )
})



// WEBPACK FOOTER //
// ./src/modules/CatalogItemDetails/CatalogItemSidebarDetails/Modal/ModalDetailTabs.tsx